import React, { useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';
import Spinner from 'design-system/Spinner';
import { useAsync } from 'react-use-v13';
import LiveInstructions from 'react-scale/components/scalers/tasks/GenericDataCollection/LiveInstructions';
import { LiveInstructionSection } from '@scale/shared/types/params/genericdatacollection';
import { Box, makeStyles } from 'react-scale/components/design-system';
import util from '@scale/frontend-lib/util';

const GREY_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPM+w8AAeEBb2u5MeEAAAAASUVORK5CYII=';

interface VideoWithLiveInstructionsProps {
  srcVideoUrl: string;
  onLoadOrError?: (activated: boolean) => void;
  caption?: string;
  onEnded?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  allowFileDownload?: boolean;
  allowPlaybackRate?: boolean;
  forceFullscreen?: boolean;
  onlyVisibleOnLoadComplete?: boolean;
  preventSkip?: boolean;
  maximumExtraPlayingTime?: number;
  disableThumbnail?: boolean;
  playing?: boolean;
  skipSourceVideoLoadingCheck?: boolean;
  onProgress?: (playedSeconds: number) => void;
  videoPlayerHeight?: string;
  videoPlayerWidth?: string;
  liveInstructions?: LiveInstructionSection[];
}

const VideoWithLiveInstructions: React.FC<VideoWithLiveInstructionsProps> = ({
  srcVideoUrl,
  onLoadOrError,
  caption = '',
  onEnded,
  onPlay = null,
  onPause = null,
  allowFileDownload = false,
  allowPlaybackRate = false,
  forceFullscreen = false,
  onlyVisibleOnLoadComplete = false,
  preventSkip = false,
  maximumExtraPlayingTime,
  disableThumbnail = false,
  playing = false,
  skipSourceVideoLoadingCheck = false,
  liveInstructions,
}) => {
  // based on notes from https://github.com/cookpete/react-player/issues/323
  const config = {
    file: {
      attributes: {
        controlsList: `${!allowFileDownload && `nodownload`} ${
          !allowPlaybackRate && `noplaybackrate`
        }`,
      },
    },
  };

  const ref = useRef<ReactPlayer>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [playedThrough, setPlayedThrough] = useState(false);
  const [startTime, setStartTime] = useState(0);

  const isMobile = util.isMobileUserAgent(window.navigator.userAgent);
  const videoStyles = useStyles(isMobile);

  const {
    loading,
    value: videoUrl,
    error,
  } = useAsync(async () => {
    if (!onlyVisibleOnLoadComplete && skipSourceVideoLoadingCheck) {
      return srcVideoUrl;
    }
    const res = await fetch(srcVideoUrl);
    if (!res.ok) {
      // HACK: this is for snowberry, who may arbitrarily remove videos
      onEnded && onEnded();

      throw new Error('Failed to load src content');
    }
    if (onlyVisibleOnLoadComplete) {
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } else {
      return srcVideoUrl;
    }
  }, [srcVideoUrl]);
  return (
    <div className="task-attachment__item task-attachment__item--video">
      {loading && (
        <div className={videoStyles.spinner}>
          <Spinner />
        </div>
      )}
      {!loading && !error && (
        <Box sx={{ width: '100%', height: '100%' }}>
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              visibility: 'visible',
            }}
          >
            <ReactPlayer
              ref={ref}
              style={{ position: 'relative', top: 0, left: 0 }}
              width={'100%'}
              height={'82%'}
              light={disableThumbnail ? GREY_PIXEL : false}
              playing={disableThumbnail || playing}
              onStart={() => {
                setStartTime(Math.round(Date.now() / 1000));
                onLoadOrError && onLoadOrError(true);
              }}
              onPlay={() => {
                onPlay && onPlay();
              }}
              onPause={() => {
                onPause && onPause();
                forceFullscreen &&
                  screenfull.isEnabled &&
                  screenfull.isFullscreen &&
                  screenfull.toggle();
              }}
              onError={function () {
                setPlayedThrough(false);
                onLoadOrError && onLoadOrError(false);
                onEnded && onEnded();
              }}
              onSeek={() => {
                if (preventSkip && !playedThrough && ref.current) {
                  const delta = ref.current.getCurrentTime() - currentTime;
                  if (delta > 0.01) {
                    ref.current.seekTo(currentTime, 'seconds');
                  }
                }
              }}
              onProgress={({ playedSeconds }) => {
                setCurrentTime(playedSeconds);
              }}
              onEnded={() => {
                if (disableThumbnail && ref.current) {
                  ref.current.showPreview();
                }
                if (forceFullscreen && screenfull.isEnabled) {
                  screenfull.toggle();
                }
                if (maximumExtraPlayingTime) {
                  const actualVideoDuration = currentTime;
                  const expectedVideoDuration =
                    Math.round(Date.now() / 1000) - startTime + maximumExtraPlayingTime;
                  if (actualVideoDuration > expectedVideoDuration) {
                    alert(
                      'Playback of this video took significantly longer than its duration.\n Please close other tabs and processes, then refresh the task.',
                    );
                    return;
                  }
                }
                setPlayedThrough(true);
                onEnded && onEnded();
              }}
              url={videoUrl}
              controls
              config={config}
            />
            {liveInstructions && videoUrl && (
              <div
                style={{
                  position: 'relative',
                  paddingTop: '5px',
                  visibility: 'visible',
                }}
              >
                <LiveInstructions
                  currentTimeInSeconds={Math.floor(currentTime)}
                  instructions={liveInstructions}
                />
              </div>
            )}
          </div>
        </Box>
      )}
      {error && (
        <div className={videoStyles.topbar}>
          <div className={videoStyles.caption}>Failed to load video. Please try refreshing.</div>
        </div>
      )}

      {!!caption && (
        <div className={videoStyles.topbar}>
          <div className={videoStyles.caption}>{caption}</div>
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  topbar: {
    position: 'absolute',
    left: '40px',
    top: '12px',
    display: 'flex',
    flexDirection: 'row',
  },
  caption: {
    background: '#FFFFFF',
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    textAlign: 'center',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
    margin: '4px',
  },
  spinner: {
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
}));

export default VideoWithLiveInstructions;
